import {
  AMPLIFY_OLD_MCLASS_ASSET,
  BOOST_LECTURA_ASSET_BK,
  BOOST_READING_ASSETS_BKS,
  BOOST_SPECIFIC_ASSET_IDS,
  FLUENCY_PRACTICE_ASSET,
  MCLASS_LECTURA_ASSET_BK,
  MCLASS_PRODUCT_NAMES,
  MCLASS_READING_ASSET_BK,
  MS_APPS_SECTION_ASSETS,
  NEW_MCLASS_ASSETS,
  POLYPAD_ASSET,
} from 'src/constants/assets.constant';
import { getEnv } from 'src/constants/config.constants';
import {
  GRADES_6_AND_ABOVE,
  GRADES_7_AND_ABOVE,
  MIDDLE_SCHOOL_GRADES,
} from 'src/constants/grades.constants';
import { Asset } from 'src/interfaces/assets.types';
import { GRADES } from 'src/interfaces/grades.types';
import {
  hasMatchingGrade,
  hasOnlyHigherMiddleSchoolGrades,
  isDefaultGradeUser,
  normalizeOnboardingGrades,
  parseGradeString,
} from 'src/utils/grades.utils';

import { getAmpConfig } from '../services/amp-config.service';

export const hasAsset = (assetBk: string, assets: Asset[] | undefined) =>
  assets ? assets.some(({ businessKey }) => businessKey === assetBk) : false;

export const hasMSGrade = (asset: Asset) => {
  if (asset.metadata.grade === undefined) {
    return false;
  }

  const grade = String(asset.metadata.grade);
  const grades = parseGradeString(grade);

  return grades.some((parsedGrade) =>
    MIDDLE_SCHOOL_GRADES.includes(parsedGrade),
  );
};

export const getBoostAssetGrades = (assets: Asset[]): string[] => {
  return assets
    .filter((asset) => BOOST_SPECIFIC_ASSET_IDS.includes(asset.businessKey))
    .map((asset) => asset.metadata.grade)
    .filter((grade) => grade !== undefined)
    .map((grade) => String(grade));
};

export const getMClassAssetGrades = (assets: Asset[]): string[] => {
  return assets
    .filter(
      (asset) =>
        asset.metadata.product &&
        MCLASS_PRODUCT_NAMES.includes(asset.metadata.product),
    )
    .map((asset) => asset.metadata.grade)
    .filter((grade) => grade !== undefined)
    .map((grade) => String(grade));
};

const shouldHideGradeBasedApp = (
  userGrades: string[],
  assets: Asset[],
  filterTriggerGrades: GRADES[],
  getAppGrades: (assets: Asset[]) => string[],
): boolean => {
  // Normalize user grades to handle both single-digit and two-digit formats
  const normalizedUserGrades = normalizeOnboardingGrades(userGrades);

  // Skip filtering for default grade users
  if (isDefaultGradeUser(normalizedUserGrades)) {
    return false;
  }

  // Only apply filtering if user has ONLY higher middle school grades (6-12+)
  if (!hasOnlyHigherMiddleSchoolGrades(normalizedUserGrades)) {
    return false;
  }

  // Check if user has grades that trigger filtering
  const userHasFilterTriggerGrade = normalizedUserGrades.some((grade) =>
    filterTriggerGrades.includes(grade),
  );

  if (!userHasFilterTriggerGrade) {
    return false;
  }

  // Get app grades using the provided function
  const appGrades = getAppGrades(assets);

  // Check if app has matching grades
  const appHasMatchingGrade = appGrades.some((grade) =>
    hasMatchingGrade(grade, filterTriggerGrades),
  );

  return !appHasMatchingGrade;
};

export const shouldHideMClassApp = (
  userGrades: string[],
  assets: Asset[],
): boolean =>
  shouldHideGradeBasedApp(
    userGrades,
    assets,
    GRADES_7_AND_ABOVE,
    getMClassAssetGrades,
  );

export const shouldHideBoostApp = (
  userGrades: string[],
  assets: Asset[],
): boolean =>
  shouldHideGradeBasedApp(
    userGrades,
    assets,
    GRADES_6_AND_ABOVE,
    getBoostAssetGrades,
  );

export const hasAssets = (
  assets: Asset[] | undefined,
  searchAssets: string[],
) => {
  if (!assets) {
    return false;
  }
  const setA = new Set(assets);
  const setB = new Set(searchAssets);
  return new Set([...setA].filter((x) => setB.has(x.businessKey))).size > 0;
};

export const cavViewMSAppsSectionAsset = (asset: Asset) => {
  const { metadata } = asset;

  // Check visibility settings - require nav.display to be explicitly true
  if (!metadata.nav?.display) {
    return false;
  }

  if (metadata.teacherOnly) {
    const isTeacherOnly =
      metadata.teacherOnly === true || metadata.teacherOnly === 'true';

    if (isTeacherOnly) {
      return false; // If it's teacher only, we don't show it
    }
  }

  return true;
};

export const getMSmClassAppAsset = (assets: Asset[]): Asset | null => {
  const mClassReadingAsset = assets.find(
    (a) => a.businessKey === MCLASS_READING_ASSET_BK,
  );
  const mClassLecturaAsset = assets.find(
    (a) => a.businessKey === MCLASS_LECTURA_ASSET_BK,
  );

  if (mClassReadingAsset && mClassLecturaAsset) {
    return {
      ...mClassReadingAsset,
      name: 'mCLASS Reading / Lectura',
      metadata: {
        ...mClassReadingAsset.metadata,
        dashboard: {
          ...mClassReadingAsset.metadata.dashboard,
          appUrl: `${getAmpConfig().amplifyMClass}/student-assessment/`,
          thumbnailUrl:
            'https://learning.amplify.com/m/7e95165056506e6e/original/mCLASS_Icon.png',
        },
      },
    };
  }

  if (mClassReadingAsset) {
    return {
      ...mClassReadingAsset,
      name: 'mCLASS Reading',
      metadata: {
        ...mClassReadingAsset.metadata,
        dashboard: {
          ...mClassReadingAsset.metadata.dashboard,
          appUrl: `${getAmpConfig().amplifyMClass}/student-assessment/`,
          thumbnailUrl:
            'https://learning.amplify.com/m/7e95165056506e6e/original/mCLASS_Icon.png',
        },
      },
    };
  }

  if (mClassLecturaAsset) {
    return {
      ...mClassLecturaAsset,
      name: 'mCLASS Lectura',
      metadata: {
        ...mClassLecturaAsset.metadata,
        dashboard: {
          ...mClassLecturaAsset.metadata.dashboard,
          appUrl: `${getAmpConfig().amplifyMClass}/student-assessment/`,
          thumbnailUrl:
            'https://learning.amplify.com/m/7e95165056506e6e/original/mCLASS_Icon.png',
        },
      },
    };
  }

  return null;
};

export const getMSBoostAppAsset = (assets: Asset[]): Asset | null => {
  const boostReadingAsset = assets.find((a) =>
    BOOST_READING_ASSETS_BKS.includes(a.businessKey),
  );
  const boostLecturaAsset = assets.find(
    (a) => a.businessKey === BOOST_LECTURA_ASSET_BK,
  );

  if (boostReadingAsset && boostLecturaAsset) {
    return {
      ...boostReadingAsset,
      name: 'Boost Reading / Lectura',
    };
  }

  if (boostReadingAsset) {
    return {
      ...boostReadingAsset,
      name: 'Boost Reading',
    };
  }

  if (boostLecturaAsset) {
    return {
      ...boostLecturaAsset,
      name: 'Boost Lectura',
    };
  }

  return null;
};

export const getUniqueAssets = (assets: Asset[]): Asset[] => {
  const processedAssets = new Set<string>();

  return assets.filter((asset) => {
    if (processedAssets.has(asset.businessKey)) {
      return false;
    }
    processedAssets.add(asset.businessKey);
    return true;
  });
};

export const getMathAdventuresGamesURLs = () => [
  {
    businessKey: '46c83777-26f8-406e-91c8-a7d9f48da0bd',
    url: `${getAmpConfig().apps}/TwelveADozen/`,
  },
  {
    businessKey: '2ff3a2c7-94fb-4d09-853b-c30e70cbce6c',
    url: `${getAmpConfig().apps}/NumberJumper/`,
  },
  {
    businessKey: '3abee149-0c7f-4510-b56c-97356b34d505',
    url: `${getAmpConfig().apps}/ConnectTheBlocks/`,
  },
];

const mathAdventureURLs = (asset: Asset) =>
  getMathAdventuresGamesURLs().find(
    (item) => item.businessKey === asset.businessKey,
  );

const normalizeMSAppAsset = (asset: Asset) => {
  if (asset.businessKey === POLYPAD_ASSET) {
    return { ...asset, name: 'Polypad Manipulatives' };
  }
  if (asset.businessKey === FLUENCY_PRACTICE_ASSET) {
    return { ...asset, name: 'Math Fluency Practice' };
  }

  const assetURLData = mathAdventureURLs(asset);
  if (assetURLData) {
    return {
      ...asset,
      metadata: {
        ...asset.metadata,
        dashboard: {
          ...asset.metadata.dashboard,
          appUrl: assetURLData.url,
        },
      },
    };
  }

  return asset;
};

const createVirtualAssets = (
  isSelfSignupUser: boolean,
  assets: Asset[],
): Asset[] => {
  const virtualAssets: Asset[] = [];

  if (!isSelfSignupUser) {
    return virtualAssets;
  }

  // Add Fluency Practice if not already present
  if (!assets.some((asset) => asset.businessKey === FLUENCY_PRACTICE_ASSET)) {
    virtualAssets.push({
      businessKey: FLUENCY_PRACTICE_ASSET,
      name: 'Math Fluency Practice',
      type: 'app',
      metadata: {
        nav: { display: true },
        dashboard: {
          appUrl: 'https://fluency.amplify.com/?sso=1',
          thumbnailUrl:
            'https://amplify-retail-public-dev.s3.amazonaws.com/assets/apps/fluency.png',
        },
      },
    });
  }

  // Add Polypad if not already present
  if (!assets.some((asset) => asset.businessKey === POLYPAD_ASSET)) {
    const polypadUrl =
      getEnv() === 'devci'
        ? 'https://polypad-docker-devci.poc.learning.amplify.com/?sso=1'
        : 'https://polypad.amplify.com/p/?sso=1';

    virtualAssets.push({
      businessKey: POLYPAD_ASSET,
      name: 'Polypad Manipulatives',
      type: 'app',
      metadata: {
        nav: { display: true },
        dashboard: {
          appUrl: polypadUrl,
          thumbnailUrl:
            'https://amplify-retail-public-dev.s3.amazonaws.com/assets/apps/polypad.png',
        },
      },
    });
  }

  return virtualAssets;
};

const filterAndNormalizeAssets = (assets: Asset[]): Asset[] => {
  // Check if user has any new mCLASS assets
  const hasNewMClassAssets = assets.some((asset) =>
    NEW_MCLASS_ASSETS.includes(asset.businessKey),
  );

  return assets
    .filter((asset) => {
      const isAppsSectionAsset = MS_APPS_SECTION_ASSETS.includes(
        asset.businessKey,
      );

      // If user has new mCLASS assets, exclude the old mCLASS asset
      if (
        hasNewMClassAssets &&
        asset.businessKey === AMPLIFY_OLD_MCLASS_ASSET
      ) {
        return false;
      }

      return isAppsSectionAsset && cavViewMSAppsSectionAsset(asset);
    })
    .map((asset) => {
      return normalizeMSAppAsset(asset);
    });
};

export const getMSAppsSectionAssets = (
  assets: Asset[],
  isSelfSignupUser = false,
  userGrades: string[] = [],
) => {
  const virtualAssets = createVirtualAssets(isSelfSignupUser, assets);
  const allAssets = [...assets, ...virtualAssets];
  const filteredAssets = filterAndNormalizeAssets(allAssets);

  // Add Boost and mCLASS apps (like the original logic)
  const msBoostAppAsset = getMSBoostAppAsset(allAssets);
  const mSmClassAppAsset = getMSmClassAppAsset(allAssets);
  const gradeBasedAssets = [
    ...(msBoostAppAsset ? [msBoostAppAsset] : []),
    ...(mSmClassAppAsset ? [mSmClassAppAsset] : []),
  ];

  const allFilteredAssets = getUniqueAssets([
    ...filteredAssets,
    ...gradeBasedAssets,
  ]);

  // Apply grade filtering as additional filter - remove apps that should be hidden
  const finalAssets = allFilteredAssets.filter((asset) => {
    // Check if this is a Boost app that should be hidden
    if (
      [...BOOST_READING_ASSETS_BKS, BOOST_LECTURA_ASSET_BK].includes(
        asset.businessKey,
      )
    ) {
      return !shouldHideBoostApp(userGrades, allAssets);
    }

    // Check if this is an mCLASS app that should be hidden
    if (
      [MCLASS_READING_ASSET_BK, MCLASS_LECTURA_ASSET_BK].includes(
        asset.businessKey,
      )
    ) {
      return !shouldHideMClassApp(userGrades, allAssets);
    }

    // For all other assets, always include them
    return true;
  });

  return finalAssets.sort((a, b) => a.name.localeCompare(b.name));
};
